import { Box, Typography, Paper, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import InfoIcon             from "@mui/icons-material/Info";
import LanIcon              from "@mui/icons-material/Lan";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import { useEffect, useState } from "react";
import API             from "../../../api";
import DeviceInfo      from "./DeviceInfo";
import Privacy         from "./Privacy";
import ChangeNameLecs  from "./ChangeNameLecs";
import ChangeIpCard    from "./ChangeIpCard";
import OtSettings      from "./OtSettings";
import OtAnomalies     from "./OtAnomalies";
import AssetInventorySettings from "./AssetInventorySettings";

const SECTIONS = [
  {
    id:          "info",
    label:       "Device Info",
    description: "Hardware, identity, IP, status and uptime",
    icon:        InfoIcon,
  },
  {
    id:          "network",
    label:       "Network",
    description: "IP address and network settings",
    icon:        LanIcon,
  },
  { id: "antlia_settings", label: "Antlia Settings", description: "Protocols, filtering and anomaly severity", icon: SettingsEthernetIcon },
  {
    id:          "asset_inventory",
    label:       "Asset Inventory",
    description: "DNS, NetBIOS and SNMP enrichment",
    icon:         SettingsEthernetIcon,
  },
];

const SidebarItem = ({ section, selected, onClick }) => {
  const Icon = section.icon;
  const isDisabled = !!section.disabled;
  return (
    <Tooltip title={isDisabled ? "OT module not licensed" : ""} placement="right" disableHoverListener={!isDisabled} arrow>
      <Box
        onClick={isDisabled ? undefined : onClick}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 2, py: 1.25, cursor: isDisabled ? "not-allowed" : "pointer", borderRadius: 1,
          backgroundColor: selected ? "rgba(69,144,149,0.14)" : "transparent",
          borderLeft: selected ? "3px solid #449095" : "3px solid transparent",
          opacity: isDisabled ? 0.4 : 1,
          transition: "all 0.15s",
          "&:hover": { backgroundColor: (!isDisabled && selected) ? "rgba(69,144,149,0.14)" : (!isDisabled ? "rgba(255,255,255,0.04)" : "transparent") },
        }}
      >
        <Icon sx={{ fontSize: 20, color: selected ? "#449095" : "#666", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: selected ? 700 : 500, color: selected ? "#e8e8e8" : "#aaa", lineHeight: 1.3 }}>
            {section.label}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {section.description}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const normalizeSection = (value) => value === "configuration" ? "info" : ["ot_settings", "ot_anomalies"].includes(value) ? "antlia_settings" : value || "info";

const DeviceSettings = ({ offlineNotify, setOfflineNotify, storedOtSettings, systemOtSettings, severityRules, setSeverityRules, severityCatalog, anomalyPrefill, onAnomalyPrefillConsumed, section, otEnabled = true }) => {
  const [selectedId,    setSelectedId]    = useState(normalizeSection(section));
  const [antliaTab, setAntliaTab] = useState(section === "ot_anomalies" ? "security" : "protocols");
  const [deviceInfo,    setDeviceInfo]    = useState({});
  const [anomalyNotify, setAnomalyNotify] = useState([]);
  const [privacy,       setPrivacy]       = useState(false);

  useEffect(() => {
    API.getDeviceInfo().then((res) => {
      setDeviceInfo(res.data);
      setAnomalyNotify(res.data.notifiche);
      setPrivacy(res.data.abilitazione === 1);
    }).catch(() => setDeviceInfo({}));
  }, []);

  useEffect(() => {
    if (section) setSelectedId(normalizeSection(section));
    if (section === "ot_anomalies") setAntliaTab("security");
  }, [section]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        minHeight: 520,
        border: "1px solid rgba(69,144,149,0.15)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "rgba(20,20,20,0.8)",
      }}
    >
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Box sx={{
        width: 220, flexShrink: 0,
        borderRight: "1px solid #222",
        backgroundColor: "#111",
        py: 1.5,
        display: "flex", flexDirection: "column", gap: 0.25,
      }}>
        <Typography sx={{ px: 2, pb: 1, fontSize: "0.62rem", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Device Settings
        </Typography>
        {SECTIONS.map((s) => (
          <SidebarItem
            key={s.id}
            section={s.id === "antlia_settings" ? { ...s, disabled: !otEnabled } : s}
            selected={selectedId === s.id}
            onClick={() => setSelectedId(s.id)}
          />
        ))}
      </Box>

      {/* ── Content ─────────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: selectedId === "antlia_settings" ? 0 : 3.5, overflowY: "auto" }}>

        {selectedId === "info" && (
          <Stack direction="column" spacing={3}>
            <DeviceInfo deviceInfo={deviceInfo} />
            <Privacy privacy={privacy} setPrivacy={setPrivacy} />
            <ChangeNameLecs />
          </Stack>
        )}

        {selectedId === "network" && (
          <Stack direction="column" spacing={3}>
            <ChangeIpCard />
          </Stack>
        )}

        {selectedId === "antlia_settings" && (
          <Box>
            <Tabs value={antliaTab} onChange={(_, value) => setAntliaTab(value)} sx={{ mb: 3, borderBottom: "1px solid #2a2a2a", "& .MuiTabs-indicator": { backgroundColor: antliaTab === "security" ? "#ffb422" : "#449095" } }}>
                <Tab value="protocols" label="Protocols & Traffic" sx={{ textTransform: "none", fontWeight: 700 }} />
              <Tab value="security" label="Security" sx={{ textTransform: "none", fontWeight: 700 }} />
            </Tabs>
            {antliaTab === "protocols" && <OtSettings embedded storedOtSettings={storedOtSettings} systemOtSettings={systemOtSettings} />}
            {antliaTab === "security" && (
              <Box sx={{ m: { xs: 1.5, md: 3 }, p: { xs: 2, md: 3 }, backgroundColor: "rgba(69,144,149,0.05)", border: "1px solid rgba(69,144,149,0.1)", borderRadius: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#449095", mb: 2 }}>Security Configuration</Typography>
                <OtAnomalies rules={severityRules} setRules={setSeverityRules} catalog={severityCatalog} prefill={anomalyPrefill} onPrefillConsumed={onAnomalyPrefillConsumed} />
              </Box>
            )}
          </Box>
        )}

        {selectedId === "asset_inventory" && <AssetInventorySettings />}

      </Box>
    </Paper>
  );
};

export default DeviceSettings;
